import { db } from '$db/local/db';
import type { Conjecture, UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, notDeleted } from './common';

export interface CreateConjectureInput {
  nodeId: UUID;
  problemNumber?: string | null;
  sessionId?: UUID | null;
  content: string;
}

export async function listConjectures(
  userId = getActiveUserId()
): Promise<Conjecture[]> {
  const rows = await db.conjectures.where('userId').equals(userId).toArray();
  return rows
    .filter(notDeleted)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export async function listConjecturesByNode(
  nodeId: UUID,
  userId = getActiveUserId()
): Promise<Conjecture[]> {
  const all = await listConjectures(userId);
  return all.filter((c) => c.nodeId === nodeId);
}

export async function createConjecture(
  input: CreateConjectureInput
): Promise<Conjecture> {
  const trimmed = input.content.trim();
  if (!trimmed) throw new Error('Conjecture content cannot be empty');
  const now = nowIso();
  const c: Conjecture = {
    id: newId(),
    userId: getActiveUserId(),
    nodeId: input.nodeId,
    problemNumber: input.problemNumber ?? null,
    sessionId: input.sessionId ?? null,
    content: trimmed,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.conjectures.add(c);
  return c;
}

export async function updateConjecture(
  id: UUID,
  content: string
): Promise<void> {
  const trimmed = content.trim();
  if (!trimmed) return;
  await db.conjectures.update(id, { content: trimmed, updatedAt: nowIso() });
}

export async function deleteConjecture(id: UUID): Promise<void> {
  await db.conjectures.update(id, { deletedAt: nowIso(), updatedAt: nowIso() });
}

export async function listConjecturesBySession(
  sessionId: UUID,
  userId = getActiveUserId()
): Promise<Conjecture[]> {
  const rows = await db.conjectures.where('userId').equals(userId).toArray();
  return rows.filter((r) => r.sessionId === sessionId && notDeleted(r));
}